import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import CrashlyticsService from '../services/crashlyticsService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to crashlytics
    try {
      const crashlytics = CrashlyticsService.getInstance();
      const userData = localStorage.getItem('userDetails');
      let userId = '';
      let userEmail = '';
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userId = user.id || user.user_id || '';
          userEmail = user.email || '';
        } catch (e) {
          // Failed to parse user data
        }
      }

      crashlytics.captureError({
        type: 'ERROR_BOUNDARY',
        severity: 'HIGH',
        message: error.message || 'Uncaught error in React component',
        stack: error.stack || errorInfo.componentStack || '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: userId,
        userEmail: userEmail,
        deviceInfo: {
          platform: navigator.platform || 'Unknown',
          browser: navigator.userAgent.split(' ')[0] || 'Unknown',
          version: navigator.appVersion || 'Unknown',
          screen: `${window.screen.width}x${window.screen.height}`,
        },
        sessionInfo: {
          sessionId: sessionStorage.getItem('sessionId') || '',
          pageLoadTime: performance.timing ? `${performance.timing.loadEventEnd - performance.timing.navigationStart}ms` : 'Unknown',
          memoryUsage: (performance as any).memory ? `${Math.round((performance as any).memory.usedJSHeapSize / 1048576)}MB` : 'Unknown',
        },
        resolved: false,
        resolvedBy: '',
        notes: `Component Stack: ${errorInfo.componentStack}`,
      });
    } catch (crashlyticsError) {
      console.error('Failed to log error to crashlytics:', crashlyticsError);
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Something went wrong
            </h1>
            
            <p className="text-gray-600 text-center mb-6">
              We're sorry, but something unexpected happened. Our team has been notified.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-gray-100 rounded-lg">
                <p className="text-sm font-mono text-red-600 mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs text-gray-600 overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

